import { LocalProjection, bearing, haversine } from "./geo";
import { otherEnd, type Graph } from "./graph";
import { distToSegment } from "./green";
import { MinHeap } from "./heap";

/** One trackpoint; `t` is epoch ms or null for untimed (anonymous) points. */
export interface TrackPoint {
  lat: number;
  lon: number;
  t: number | null;
}

/** A run of points from one trace (timed) or one bag of anonymous points (untimed). */
export type Segment = TrackPoint[];

/** Running steps: 2.0 m/s (8:20 min/km, above brisk walking) up to 6.5 m/s (2:34, strides). */
export const MIN_RUN_SPEED = 2.0;
export const MAX_RUN_SPEED = 6.5;
export const MIN_WALK_SPEED = 0.8; // m/s ≈ 21 min/km; slower is standing
/**
 * Run-only popularity (ADR 0005, 2026-11-04): a trace counts only if its speed profile
 * (`traceProfile`) is a run. Median ≤ 4.5 m/s (3:42 min/km), p85 ≤ 5.5 m/s (3:02 min/km), at most
 * 25% of moving time above 6 m/s, at least half of it at running speed, and ≥ 60 s moving.
 */
export const RUN_MAX_MEDIAN = 4.5;
export const RUN_MAX_P85 = 5.5;
export const RUN_MAX_FAST_SHARE = 0.25;
export const RUN_MIN_RUN_SHARE = 0.5;
export const RUN_MIN_MOVING_S = 60;
/** Clipped pieces of one trace are rejoined when the next starts ≤ this many seconds later. */
export const STITCH_MAX_GAP_S = 30;
/** Walking steps of a run count nothing (run-only, ADR 0005 2026-11-04). */
export const WALK_WEIGHT = 0;
/**
 * Untimed points (diagnostics and v1 tests only; production drops them: speed unknown).
 * @deprecated Diagnostics only: run-only popularity (ADR 0005, 2026-11-04) never counts untimed
 * points. Kept for tests comparing against v1.
 */
export const UNTIMED_WEIGHT = 0.3;
const MATCH_TIMED_M = 25;
const MATCH_UNTIMED_M = 15;
const HEADING_TOL_DEG = 45;
const CELL_M = 50;

/**
 * Parses OSM API trackpoints GPX. Uses a tolerant regex scan (no DOMParser in workers).
 * Each <trkseg> becomes one segment; untimed points are grouped into their own segments.
 */
export function parseTrackpointsGpx(xml: string): Segment[] {
  const segs: Segment[] = [];
  const segRe = /<trkseg>([\s\S]*?)<\/trkseg>/g;
  // Either self-closing (<trkpt …/>) or with a body (<trkpt …>…</trkpt>).
  const ptRe = /<trkpt\s+([^>]*?)(?:\/>|>([\s\S]*?)<\/trkpt>)/g;
  const attr = (s: string, k: string) => {
    const m = new RegExp(`${k}="([^"]+)"`).exec(s);
    return m ? Number(m[1]) : NaN;
  };
  for (const m of xml.matchAll(segRe)) {
    const seg: Segment = [];
    for (const p of (m[1] ?? "").matchAll(ptRe)) {
      const lat = attr(p[1] ?? "", "lat");
      const lon = attr(p[1] ?? "", "lon");
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      const tm = /<time>([^<]+)<\/time>/.exec(p[2] ?? "");
      const t = tm ? Date.parse(tm[1] as string) : null;
      seg.push({ lat, lon, t: t !== null && Number.isFinite(t) ? t : null });
    }
    if (seg.length > 0) segs.push(seg);
  }
  return segs;
}

const MAX_GAP_S = 120;
/** traceProfile: speeds are displacements over windows of at least this many seconds. */
export const PROFILE_WINDOW_S = 10;
/** traceProfile: moving time faster than this (m/s, 2:47 min/km) counts as `fastShare`. */
export const PROFILE_FAST_SPEED = 6;

/** Speed profile of one timed trace (ADR 0005, run-only). Speeds in m/s, shares of time. */
export interface TraceProfile {
  /** Moving time (windows at ≥ MIN_WALK_SPEED), seconds. */
  movingS: number;
  /** Moving → stopped transitions (diagnostics: they do not separate runs from rides). */
  stops: number;
  /** Stopped time / (moving + stopped). */
  stopShare: number;
  /** Quantiles of the moving windows' speeds (NaN without any). */
  median: number;
  p85: number;
  p95: number;
  /** Shares of moving time: > PROFILE_FAST_SPEED, MIN_RUN..MAX_RUN_SPEED, below MIN_RUN_SPEED. */
  fastShare: number;
  runShare: number;
  walkShare: number;
}

let winV = new Float64Array(256);
let winDt = new Float64Array(256);
let winSort = new Float64Array(256);

/**
 * Speed profile of a trace over windows of ≥ PROFILE_WINDOW_S: from an anchor fix, the first fix
 * at least that much later closes a window (displacement / time), so ±3 m jitter reads below
 * walking speed. Gaps (dt ≤ 0 or > 2 min, untimed fixes) restart the anchor; a tail window
 * shorter than half the window is dropped. No allocation beyond growing module scratch.
 */
export function traceProfile(seg: Segment): TraceProfile {
  let n = 0;
  const push = (v: number, dt: number) => {
    if (n === winV.length) {
      const grow = (a: Float64Array) => {
        const b = new Float64Array(a.length * 2);
        b.set(a);
        return b;
      };
      winV = grow(winV);
      winDt = grow(winDt);
      winSort = new Float64Array(winV.length);
    }
    winV[n] = v;
    winDt[n++] = dt;
  };
  let a = -1; // anchor index
  let prev = -1; // previous timed index in the current run
  const close = (b: number, min: number) => {
    if (a < 0) return;
    const pa = seg[a] as TrackPoint;
    const pb = seg[b] as TrackPoint;
    const dt = ((pb.t as number) - (pa.t as number)) / 1000;
    if (dt >= min && dt > 0) push(haversine(pa.lat, pa.lon, pb.lat, pb.lon) / dt, dt);
  };
  for (let i = 0; i < seg.length; i++) {
    const p = seg[i] as TrackPoint;
    if (p.t === null) {
      if (prev >= 0 && prev !== a) close(prev, PROFILE_WINDOW_S / 2);
      a = prev = -1;
      continue;
    }
    if (prev >= 0) {
      const dt = (p.t - ((seg[prev] as TrackPoint).t as number)) / 1000;
      if (dt <= 0 || dt > MAX_GAP_S) {
        if (prev !== a) close(prev, PROFILE_WINDOW_S / 2);
        a = prev = i;
        continue;
      }
    }
    if (a < 0) a = i;
    else if ((p.t - ((seg[a] as TrackPoint).t as number)) / 1000 >= PROFILE_WINDOW_S) {
      close(i, PROFILE_WINDOW_S);
      a = i;
    }
    prev = i;
  }
  if (prev >= 0 && a >= 0 && prev !== a) close(prev, PROFILE_WINDOW_S / 2);
  let movingS = 0;
  let stopS = 0;
  let fast = 0;
  let run = 0;
  let walk = 0;
  let m = 0;
  let stops = 0;
  for (let k = 0; k < n; k++) {
    const v = winV[k] as number;
    const dt = winDt[k] as number;
    if (v < MIN_WALK_SPEED) {
      if (k > 0 && (winV[k - 1] as number) >= MIN_WALK_SPEED) stops++;
      stopS += dt;
      continue;
    }
    movingS += dt;
    winSort[m++] = v;
    if (v > PROFILE_FAST_SPEED) fast += dt;
    if (v >= MIN_RUN_SPEED && v <= MAX_RUN_SPEED) run += dt;
    else if (v < MIN_RUN_SPEED) walk += dt;
  }
  const vs = winSort.subarray(0, m).sort();
  const q = (f: number) => (m > 0 ? (vs[Math.floor(f * (m - 1))] as number) : NaN);
  return {
    movingS,
    stops,
    stopShare: movingS + stopS > 0 ? stopS / (movingS + stopS) : 0,
    median: q(0.5),
    p85: q(0.85),
    p95: q(0.95),
    fastShare: movingS > 0 ? fast / movingS : 0,
    runShare: movingS > 0 ? run / movingS : 0,
    walkShare: movingS > 0 ? walk / movingS : 0,
  };
}

/**
 * Is this trace a run (ADR 0005, run-only)? Timed, ≥ RUN_MIN_MOVING_S moving, median ≤
 * RUN_MAX_MEDIAN, p85 ≤ RUN_MAX_P85, fast share ≤ RUN_MAX_FAST_SHARE and run share ≥
 * RUN_MIN_RUN_SHARE. Commuter cycling has a stop-dragged median but a cycling p85; walks fail
 * the run share; short pieces (speed unknown) fail the moving time. Stops are not penalised.
 */
export function isRunTrace(seg: Segment): boolean {
  const f = traceProfile(seg);
  return (
    f.movingS >= RUN_MIN_MOVING_S &&
    f.median <= RUN_MAX_MEDIAN &&
    f.p85 <= RUN_MAX_P85 &&
    f.fastShare <= RUN_MAX_FAST_SHARE &&
    f.runShare >= RUN_MIN_RUN_SHARE
  );
}

/**
 * Drops repeated uploads of the same trace: segments with the same first and last point (lat,
 * lon, time) and length. Keeps the first copy, in input order.
 */
export function dedupeTraces(segs: Segment[]): Segment[] {
  const seen = new Set<string>();
  const out: Segment[] = [];
  for (const s of segs) {
    if (s.length === 0) continue;
    const a = s[0] as TrackPoint;
    const b = s[s.length - 1] as TrackPoint;
    const k = `${a.lat},${a.lon},${a.t},${b.lat},${b.lon},${b.t},${s.length}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
  }
  return out;
}

/**
 * Rejoins pieces of one trace that the trackpoints API clipped at bbox borders (ADR 0005), after
 * `dedupeTraces`. Piece b follows piece a when b's first fix is 0 < Δt ≤ STITCH_MAX_GAP_S after
 * a's last and within max(30 m, 15 m/s × Δt) of it, and the link is unique both ways (two people
 * running side by side stay apart). Pieces without timed ends pass through. Chains are returned
 * in the order of their first piece.
 */
export function stitchTraces(segs: Segment[]): Segment[] {
  const ps = dedupeTraces(segs);
  const n = ps.length;
  const t0 = new Float64Array(n);
  const order: number[] = [];
  for (let i = 0; i < n; i++) {
    const t = (ps[i] as Segment)[0]?.t ?? null;
    t0[i] = t ?? NaN;
    if (t !== null) order.push(i);
  }
  order.sort((x, y) => (t0[x] as number) - (t0[y] as number) || x - y);
  const starts = Float64Array.from(order, (i) => t0[i] as number);
  const next = new Int32Array(n).fill(-1);
  const nNext = new Int32Array(n);
  const nPrev = new Int32Array(n);
  for (let i = 0; i < n; i++) {
    const s = ps[i] as Segment;
    const e = s[s.length - 1] as TrackPoint;
    if (e.t === null) continue;
    // First start strictly after e.t.
    let lo = 0;
    let hi = starts.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if ((starts[mid] as number) <= e.t) lo = mid + 1;
      else hi = mid;
    }
    for (let k = lo; k < starts.length; k++) {
      const dt = ((starts[k] as number) - e.t) / 1000;
      if (dt > STITCH_MAX_GAP_S) break;
      const j = order[k] as number;
      if (j === i) continue;
      const f = (ps[j] as Segment)[0] as TrackPoint;
      if (haversine(e.lat, e.lon, f.lat, f.lon) > Math.max(30, 15 * dt)) continue;
      nNext[i] = (nNext[i] as number) + 1;
      nPrev[j] = (nPrev[j] as number) + 1;
      next[i] = j;
    }
  }
  const link = new Int32Array(n).fill(-1);
  const hasPrev = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const j = next[i] as number;
    if (nNext[i] === 1 && nPrev[j] === 1) {
      link[i] = j;
      hasPrev[j] = 1;
    }
  }
  const out: Segment[] = [];
  const done = new Uint8Array(n);
  const chain = (i: number) => {
    if (link[i] === -1) {
      done[i] = 1;
      out.push(ps[i] as Segment);
      return;
    }
    const t: Segment = [];
    for (let k = i; k >= 0 && !done[k]; k = link[k] as number) {
      done[k] = 1;
      for (const p of ps[k] as Segment) t.push(p);
    }
    out.push(t);
  };
  for (let i = 0; i < n; i++) if (!hasPrev[i]) chain(i);
  // Time running backwards inside pieces could close a cycle (no head): keep those pieces too.
  for (let i = 0; i < n; i++) if (!done[i]) chain(i);
  return out;
}

/**
 * Run traces (ADR 0005, run-only): timed segments are deduped and stitched (`stitchTraces`) and
 * kept only if `isRunTrace`. Fully untimed segments are collected apart (production drops them).
 */
export function runTraces(segs: Segment[]): {
  runs: Segment[];
  untimed: TrackPoint[];
  rejected: number;
} {
  const timed: Segment[] = [];
  const untimed: TrackPoint[] = [];
  for (const seg of segs) {
    if (seg.every((p) => p.t === null)) for (const p of seg) untimed.push(p);
    else timed.push(seg);
  }
  const runs: Segment[] = [];
  let rejected = 0;
  for (const s of stitchTraces(timed)) {
    if (isRunTrace(s)) runs.push(s);
    else rejected++;
  }
  return { runs, untimed, rejected };
}

/**
 * Splits timed segments into running-speed pieces (MIN_RUN_SPEED–MAX_RUN_SPEED) and walking-speed
 * pieces (MIN_WALK_SPEED–MIN_RUN_SPEED); untimed segments pass through. Steps outside both bands
 * (standing, cycling) are dropped, gaps > 2 min split, and a piece ends where the band changes.
 * Pieces need ≥ 3 points. No trace filter: pass `runTraces(…).runs` for run-only popularity.
 */
export function runningPieces(segs: Segment[]): {
  timed: Segment[];
  walk: Segment[];
  untimed: TrackPoint[];
} {
  const timed: Segment[] = [];
  const walk: Segment[] = [];
  const untimed: TrackPoint[] = [];
  for (const seg of segs) {
    if (seg.every((p) => p.t === null)) {
      untimed.push(...seg);
      continue;
    }
    let cur: Segment = [];
    let band = BAND_NONE; // band of the steps in `cur` (none while cur has < 2 points)
    const flush = () => {
      if (cur.length >= 3) (band === BAND_RUN ? timed : walk).push(cur);
      cur = [];
      band = BAND_NONE;
    };
    for (let i = 0; i < seg.length; i++) {
      const p = seg[i] as TrackPoint;
      if (p.t === null) {
        flush();
        continue;
      }
      const prev = cur[cur.length - 1];
      if (!prev) {
        cur.push(p);
        continue;
      }
      const dt = (p.t - (prev.t as number)) / 1000;
      // Gaps > 2 min split the trace; a step outside both bands is dropped.
      const b =
        dt <= 0 || dt > MAX_GAP_S
          ? BAND_NONE
          : speedBand(haversine(prev.lat, prev.lon, p.lat, p.lon) / dt);
      if (b === BAND_NONE) {
        flush();
        cur.push(p);
        continue;
      }
      if (band !== BAND_NONE && b !== band) {
        // Band change: the shared point ends this piece and starts the next.
        flush();
        cur.push(prev);
      }
      band = b;
      cur.push(p);
    }
    flush();
  }
  return { timed, walk, untimed };
}

const BAND_NONE = 0;
const BAND_WALK = 1;
const BAND_RUN = 2;
const speedBand = (v: number) =>
  v >= MIN_RUN_SPEED && v <= MAX_RUN_SPEED
    ? BAND_RUN
    : v >= MIN_WALK_SPEED && v < MIN_RUN_SPEED
      ? BAND_WALK
      : BAND_NONE;

/** Sub-cell grid steps per degree (0.01°, as `sources/trackpoints.ts` SUB_DEG). */
const SUB_PER_DEG = 100;
/** Floor in sub-cell units, safe for edges like 1186 * 0.05 = 59.300000000000004. */
const subIndex = (deg: number) => Math.floor(deg * SUB_PER_DEG + 1e-7);
const subName = (i: number, j: number) =>
  `${(i / SUB_PER_DEG).toFixed(2)}_${(j / SUB_PER_DEG).toFixed(2)}`;

/** Name of the 0.01° trace sub-cell holding a point: its south-west corner, e.g. `59.32_18.10`. */
export function subCellKey(lat: number, lon: number): string {
  return subName(subIndex(lat), subIndex(lon));
}

/**
 * Names of the 0.01° sub-cells that edge geometry passes through (ADR 0005: trace requests skip
 * sub-cells without paths, such as open sea). Exact grid traversal of every geometry segment.
 */
export function edgeSubCells(g: Graph): Set<string> {
  const seen = new Set<number>();
  // Cell key as one number: lat index × 2^17 + lon index (|lon index| ≤ 36,000 < 2^16).
  const add = (i: number, j: number) => seen.add(i * 131_072 + j);
  for (let e = 0; e < g.edgeCount; e++) {
    for (let k = g.geomStart[e] as number; k < (g.geomStart[e + 1] as number) - 1; k++) {
      const y0 = (g.geomLat[k] as number) * SUB_PER_DEG;
      const x0 = (g.geomLon[k] as number) * SUB_PER_DEG;
      const y1 = (g.geomLat[k + 1] as number) * SUB_PER_DEG;
      const x1 = (g.geomLon[k + 1] as number) * SUB_PER_DEG;
      let i = subIndex(g.geomLat[k] as number);
      let j = subIndex(g.geomLon[k] as number);
      const ei = subIndex(g.geomLat[k + 1] as number);
      const ej = subIndex(g.geomLon[k + 1] as number);
      add(i, j);
      const dx = x1 - x0;
      const dy = y1 - y0;
      const sj = ej > j ? 1 : -1;
      const si = ei > i ? 1 : -1;
      // Parameter t (0..1) at the next vertical / horizontal grid line, and per cell crossed.
      let tx = dx === 0 ? Infinity : (j + (sj > 0 ? 1 : 0) - x0) / dx;
      let ty = dy === 0 ? Infinity : (i + (si > 0 ? 1 : 0) - y0) / dy;
      const ddx = dx === 0 ? Infinity : Math.abs(1 / dx);
      const ddy = dy === 0 ? Infinity : Math.abs(1 / dy);
      // Exactly |Δi| + |Δj| steps, so rounding can never overshoot the end cell.
      for (let steps = Math.abs(ei - i) + Math.abs(ej - j); steps > 0; steps--) {
        if (i === ei || (j !== ej && tx < ty)) {
          j += sj;
          tx += ddx;
        } else {
          i += si;
          ty += ddy;
        }
        add(i, j);
      }
    }
  }
  const out = new Set<string>();
  for (const key of seen) {
    const i = Math.round(key / 131_072);
    out.add(subName(i, key - i * 131_072));
  }
  return out;
}

/** Grid index over edge geometry segments, for map-matching. */
export class EdgeIndex {
  private readonly cells = new Map<number, number[]>(); // flat: [edge, geomIndex, ...]
  readonly proj: LocalProjection;
  private readonly x: Float64Array;
  private readonly y: Float64Array;
  /** Projected length of an edge's geometry up to each vertex (0 at the edge's first vertex). */
  private readonly cum: Float64Array;

  constructor(readonly g: Graph) {
    this.proj = new LocalProjection({ lat: g.lat[0] ?? 0, lon: g.lon[0] ?? 0 });
    const n = g.geomLat.length;
    this.x = new Float64Array(n);
    this.y = new Float64Array(n);
    this.cum = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      const p = this.proj.toXY({ lat: g.geomLat[i] as number, lon: g.geomLon[i] as number });
      this.x[i] = p.x;
      this.y[i] = p.y;
    }
    for (let e = 0; e < g.edgeCount; e++) {
      for (let i = (g.geomStart[e] as number) + 1; i < (g.geomStart[e + 1] as number); i++)
        this.cum[i] =
          (this.cum[i - 1] as number) +
          Math.hypot(
            (this.x[i] as number) - (this.x[i - 1] as number),
            (this.y[i] as number) - (this.y[i - 1] as number),
          );
      for (let i = g.geomStart[e] as number; i < (g.geomStart[e + 1] as number) - 1; i++) {
        const x0 = Math.min(this.x[i] as number, this.x[i + 1] as number);
        const x1 = Math.max(this.x[i] as number, this.x[i + 1] as number);
        const y0 = Math.min(this.y[i] as number, this.y[i + 1] as number);
        const y1 = Math.max(this.y[i] as number, this.y[i + 1] as number);
        for (let cx = Math.floor(x0 / CELL_M); cx <= Math.floor(x1 / CELL_M); cx++) {
          for (let cy = Math.floor(y0 / CELL_M); cy <= Math.floor(y1 / CELL_M); cy++) {
            const k = cx * 1_000_003 + cy;
            const list = this.cells.get(k);
            if (list) list.push(e, i);
            else this.cells.set(k, [e, i]);
          }
        }
      }
    }
  }

  /**
   * Nearest edge within `maxM` whose direction is within ±`tolDeg` (default 45°, either way) of
   * `headingDeg` (pass null to ignore heading). Returns -1 if none.
   */
  match(
    lat: number,
    lon: number,
    maxM: number,
    headingDeg: number | null,
    tolDeg = HEADING_TOL_DEG,
  ): number {
    const g = this.g;
    const { x, y } = this.proj.toXY({ lat, lon });
    let best = -1;
    let bestD = maxM;
    const r = Math.ceil(maxM / CELL_M);
    const cx0 = Math.floor(x / CELL_M);
    const cy0 = Math.floor(y / CELL_M);
    for (let cx = cx0 - r; cx <= cx0 + r; cx++) {
      for (let cy = cy0 - r; cy <= cy0 + r; cy++) {
        const list = this.cells.get(cx * 1_000_003 + cy);
        if (!list) continue;
        for (let j = 0; j < list.length; j += 2) {
          const e = list[j] as number;
          const i = list[j + 1] as number;
          const d = distToSegment(
            x,
            y,
            this.x[i] as number,
            this.y[i] as number,
            this.x[i + 1] as number,
            this.y[i + 1] as number,
          );
          if (d > bestD) continue;
          if (headingDeg !== null) {
            const segHeading = bearing(
              { lat: g.geomLat[i] as number, lon: g.geomLon[i] as number },
              { lat: g.geomLat[i + 1] as number, lon: g.geomLon[i + 1] as number },
            );
            let diff = Math.abs(segHeading - headingDeg) % 180;
            if (diff > 90) diff = 180 - diff;
            if (diff > tolDeg) continue;
          }
          bestD = d;
          best = e;
        }
      }
    }
    return best;
  }

  /**
   * HMM candidates: every edge with a geometry segment within `maxM` of the point, nearest first
   * (ties by edge id), at most `max`. Writes edge, distance and offset along the edge (metres from
   * `u`, scaled to `g.len`) to `outE/outD/outOff` from index `at`. Returns the count.
   */
  candidates(
    lat: number,
    lon: number,
    maxM: number,
    max: number,
    outE: Int32Array,
    outD: Float64Array,
    outOff: Float64Array,
    at: number,
  ): number {
    const g = this.g;
    const proj = this.proj;
    const x = (lon - proj.origin.lon) * proj.mPerDegLon;
    const y = (lat - proj.origin.lat) * proj.mPerDegLat;
    const sE = CAND_E;
    const sD = CAND_D;
    const sO = CAND_OFF;
    let n = 0;
    // Only cells the query square touches: a segment sits in every cell its bbox overlaps.
    const cx1 = Math.floor((x + maxM) / CELL_M);
    const cy1 = Math.floor((y + maxM) / CELL_M);
    for (let cx = Math.floor((x - maxM) / CELL_M); cx <= cx1; cx++) {
      for (let cy = Math.floor((y - maxM) / CELL_M); cy <= cy1; cy++) {
        const list = this.cells.get(cx * 1_000_003 + cy);
        if (!list) continue;
        for (let j = 0; j < list.length; j += 2) {
          const e = list[j] as number;
          const i = list[j + 1] as number;
          const ax = this.x[i] as number;
          const ay = this.y[i] as number;
          const bx = this.x[i + 1] as number;
          const by = this.y[i + 1] as number;
          const d = distToSegment(x, y, ax, ay, bx, by);
          if (d > maxM) continue;
          let k = 0;
          while (k < n && sE[k] !== e) k++;
          // Same edge: keep its nearest segment (the first in geometry order on a tie).
          if (k < n) {
            const dk = sD[k] as number;
            if (d > dk || (d === dk && i >= (CAND_SEG[k] as number))) continue;
          } else if (n === CAND_SCRATCH) {
            // Scratch full (very dense area): replace the farthest edge if this one is nearer.
            k = 0;
            for (let q = 1; q < n; q++) if ((sD[q] as number) > (sD[k] as number)) k = q;
            if (d >= (sD[k] as number)) continue;
          } else n++;
          const dx = bx - ax;
          const dy = by - ay;
          const l2 = dx * dx + dy * dy;
          const f = l2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / l2)) : 0;
          const s0 = g.geomStart[e] as number;
          const total = this.cum[(g.geomStart[e + 1] as number) - 1] as number;
          const along = (this.cum[i] as number) - (this.cum[s0] as number) + f * Math.hypot(dx, dy);
          sE[k] = e;
          sD[k] = d;
          CAND_SEG[k] = i;
          sO[k] = total > 0 ? (along / total) * (g.len[e] as number) : 0;
        }
      }
    }
    // Insertion sort by (distance, edge id) into the output, keeping the first `max`.
    let m = 0;
    for (let k = 0; k < n; k++) {
      const e = sE[k] as number;
      const d = sD[k] as number;
      let p = Math.min(m, max - 1);
      if (m === max) {
        const le = outE[at + max - 1] as number;
        const ld = outD[at + max - 1] as number;
        if (d > ld || (d === ld && e > le)) continue;
      } else m++;
      while (p > 0) {
        const pe = outE[at + p - 1] as number;
        const pd = outD[at + p - 1] as number;
        if (pd < d || (pd === d && pe < e)) break;
        outE[at + p] = pe;
        outD[at + p] = pd;
        outOff[at + p] = outOff[at + p - 1] as number;
        p--;
      }
      outE[at + p] = e;
      outD[at + p] = d;
      outOff[at + p] = sO[k] as number;
    }
    return m;
  }
}

/** Scratch for `EdgeIndex.candidates` (edges within reach of one point, before the top-k cut). */
const CAND_SCRATCH = 64;
const CAND_E = new Int32Array(CAND_SCRATCH);
const CAND_D = new Float64Array(CAND_SCRATCH);
const CAND_OFF = new Float64Array(CAND_SCRATCH);
const CAND_SEG = new Int32Array(CAND_SCRATCH);

/** HMM emission: Gaussian on the point-to-edge distance, σ in metres (ADR 0005, HMM). */
export const HMM_SIGMA_M = 8;
/** HMM transition: exponential on |route − great-circle distance|, β in metres. */
export const HMM_BETA_M = 1.5;
/** Candidate edges lie within this distance of a point… */
export const HMM_RADIUS_M = 30;
/** …at most this many per point (nearest first). */
export const HMM_MAX_CANDIDATES = 8;
/** Points are thinned to about this spacing before matching. */
export const HMM_SPACING_M = 10;
/**
 * A matched edge counts only if the route covers at least min(share × length, metres) of it, so
 * a junction grazed by noise (in and back out through one node) does not count. A chain that
 * stays on one edge keeps it.
 */
export const HMM_COVER_SHARE = 0.5;
/**
 * Whole traces: a step's speed band is measured over at least this window (seconds), between the
 * means of up to HMM_SPEED_SMOOTH fixes at each end (so jitter while standing does not move).
 */
export const HMM_SPEED_WINDOW_S = 15;
export const HMM_SPEED_SMOOTH = 5;
export const HMM_COVER_M = 15;

/** Growable typed-array helpers (keep contents). */
function growI32(a: Int32Array<ArrayBuffer>, n: number): Int32Array<ArrayBuffer> {
  if (a.length >= n) return a;
  const b = new Int32Array(Math.max(n, a.length * 2));
  b.set(a);
  return b;
}
function growF64(a: Float64Array<ArrayBuffer>, n: number): Float64Array<ArrayBuffer> {
  if (a.length >= n) return a;
  const b = new Float64Array(Math.max(n, a.length * 2));
  b.set(a);
  return b;
}

/**
 * Map-matching v2: an HMM in the style of Newson & Krumm (2009) for one timed piece at a time.
 *
 * - Points are thinned to ≥ HMM_SPACING_M apart. Candidates are the ≤ 8 nearest edges within
 *   30 m; points without any are skipped.
 * - Emission: log N(distance; 0, σ). Transition: −|route − great-circle| / β, where the route
 *   distance between the two snapped positions comes from a Dijkstra bounded at 2 × great-circle
 *   + 50 m (longer is infeasible). Same-edge and shared-node routes are taken directly when a
 *   straight-line lower bound proves nothing shorter exists; otherwise node trees are cached
 *   per piece.
 * - Viterbi in log space. When no transition into a point is feasible the chain breaks and
 *   restarts there; no route is filled across a break.
 * - Output: `path[0..n)` from `matchPiece`, the edges of the matched route in order (including
 *   the route edges between candidates, so gaps fill), with -1 between chains.
 */
export class HmmMatcher {
  /** Matched route of the last piece (see `matchPiece`); -1 separates chains. */
  path = new Int32Array(256);
  /** Weight of each `path` entry: 1, or the walking weight for walk-only edges in banded mode. */
  pathW = new Float32Array(256);
  /** Chosen edge per thinned point that had candidates, for the last piece. */
  chosen = new Int32Array(256);
  chosenCount = 0;

  private readonly sigma: number;
  private readonly beta: number;
  private readonly spacing: number;
  // Covered offset range [covLo, covHi] per (band class, edge) in the current piece, at
  // c · edgeCount + e (class 0 running, 1 walking; valid when covStamp matches).
  private readonly covLo: Float64Array;
  private readonly covHi: Float64Array;
  private readonly covStamp: Int32Array;
  // Per step s (thinned point with candidates), candidate k at s·K + k.
  private cE = new Int32Array(256 * HMM_MAX_CANDIDATES);
  private cD = new Float64Array(256 * HMM_MAX_CANDIDATES);
  private cOff = new Float64Array(256 * HMM_MAX_CANDIDATES);
  private score = new Float64Array(256 * HMM_MAX_CANDIDATES);
  private back = new Int32Array(256 * HMM_MAX_CANDIDATES);
  private cN = new Int32Array(256);
  private pick = new Int32Array(256);
  private sLat = new Float64Array(256);
  private sLon = new Float64Array(256);
  private sT = new Float64Array(256);
  private sIdx = new Int32Array(256);
  // Bounded Dijkstra scratch.
  private readonly dist: Float64Array;
  private readonly visit: Int32Array;
  private readonly predE: Int32Array;
  private readonly heap = new MinHeap(256);
  private visitStamp = 0;
  // Node trees cached per piece: node → slot; slot → [start, start + len) in the pool.
  private readonly treeStamp: Int32Array;
  private readonly treeSlot: Int32Array;
  private pieceStamp = 0;
  private tRadius = new Float64Array(64);
  private tStart = new Int32Array(64);
  private tLen = new Int32Array(64);
  private slots = 0;
  private poolNode = new Int32Array(4096);
  private poolDist = new Float64Array(4096);
  private poolPred = new Int32Array(4096);
  private poolLen = 0;
  // Result of the last `route` call: the node pair the route went through (-1: along one edge).
  private viaA = -1;
  private viaB = -1;
  private revBuf = new Int32Array(64);
  private readonly bestV = new Float64Array(HMM_MAX_CANDIDATES);
  private readonly bestArg = new Int32Array(HMM_MAX_CANDIDATES);
  private readonly order = new Int32Array(HMM_MAX_CANDIDATES);
  // Two trees loaded into node-indexed arrays for O(1) lookups (slot, load id, node → pool index).
  private readonly lkStamp: Int32Array; // [w · nodeCount + node]
  private readonly lkIdx: Int32Array;
  private readonly lkSlot = new Int32Array([-1, -1]);
  private readonly lkId = new Int32Array(2);
  private lkNext = 0;

  constructor(
    readonly g: Graph,
    readonly idx: EdgeIndex,
    opts: { sigma?: number; beta?: number; spacing?: number } = {},
  ) {
    this.sigma = opts.sigma ?? HMM_SIGMA_M;
    this.beta = opts.beta ?? HMM_BETA_M;
    this.spacing = opts.spacing ?? HMM_SPACING_M;
    this.covLo = new Float64Array(2 * g.edgeCount);
    this.covHi = new Float64Array(2 * g.edgeCount);
    this.covStamp = new Int32Array(2 * g.edgeCount);
    const n = g.nodeCount;
    this.dist = new Float64Array(n);
    this.visit = new Int32Array(n);
    this.predE = new Int32Array(n);
    this.treeStamp = new Int32Array(n);
    this.treeSlot = new Int32Array(n);
    this.lkStamp = new Int32Array(2 * n);
    this.lkIdx = new Int32Array(2 * n);
  }

  /**
   * Matches one timed piece. Returns the length of `path`: matched edges in route order, with
   * consecutive repeats removed and -1 between chains; `pathW` holds each entry's weight.
   *
   * With `banded`, the piece is a whole trace: each matched step (between thinned points) is
   * classed by its own speed — running 1, walking `walkWeight`, standing and cycling 0 — and the
   * route edges of that step are covered in that class. An edge's weight is the highest class
   * that passes the coverage rule on it. Without `banded`, every step counts 1.
   */
  matchPiece(piece: Segment, banded = false, walkWeight = WALK_WEIGHT): number {
    const K = HMM_MAX_CANDIDATES;
    const idx = this.idx;
    this.pieceStamp++;
    this.slots = 0;
    this.poolLen = 0;
    this.lkSlot[0] = this.lkSlot[1] = -1;
    // 1. Thin and collect candidates.
    let steps = 0;
    let lastLat = NaN;
    let lastLon = NaN;
    for (let i = 0; i < piece.length; i++) {
      const p = piece[i] as TrackPoint;
      if (i > 0 && haversine(lastLat, lastLon, p.lat, p.lon) < this.spacing) continue;
      lastLat = p.lat;
      lastLon = p.lon;
      if ((steps + 1) * K > this.cE.length) this.growSteps((steps + 1) * 2);
      const c = idx.candidates(
        p.lat,
        p.lon,
        HMM_RADIUS_M,
        K,
        this.cE,
        this.cD,
        this.cOff,
        steps * K,
      );
      if (c === 0) continue;
      this.cN[steps] = c;
      this.sLat[steps] = p.lat;
      this.sLon[steps] = p.lon;
      this.sT[steps] = p.t ?? NaN;
      this.sIdx[steps] = i;
      steps++;
    }
    this.chosenCount = steps;
    if (steps === 0) return 0;
    // 2. Viterbi.
    const { cD, score, back, cN } = this;
    const inv2s2 = 0.5 / (this.sigma * this.sigma);
    const invBeta = 1 / this.beta;
    for (let j = 0; j < (cN[0] as number); j++) {
      const d = cD[j] as number;
      score[j] = -d * d * inv2s2;
      back[j] = -1;
    }
    for (let s = 1; s < steps; s++) {
      const gc = haversine(
        this.sLat[s - 1] as number,
        this.sLon[s - 1] as number,
        this.sLat[s] as number,
        this.sLon[s] as number,
      );
      const cap = 2 * gc + 50;
      const p0 = (s - 1) * K;
      const nPrev = cN[s - 1] as number;
      const nCur = cN[s] as number;
      // Source-major (each source's two end-node trees stay loaded); per target, the first
      // source with the strictly best score wins, as in a target-major loop.
      const bestV = this.bestV;
      const bestArg = this.bestArg;
      for (let j = 0; j < nCur; j++) {
        bestV[j] = -Infinity;
        bestArg[j] = -1;
      }
      // Best sources first: a transition only lowers the score, so a source scoring ≤ a
      // target's best so far cannot win it (ties keep the earlier source, as before).
      const order = this.order;
      for (let i = 0; i < nPrev; i++) {
        const si = score[p0 + i] as number;
        let k = i;
        while (k > 0 && (score[p0 + (order[k - 1] as number)] as number) < si) {
          order[k] = order[k - 1] as number;
          k--;
        }
        order[k] = i;
      }
      for (let oi = 0; oi < nPrev; oi++) {
        const i = order[oi] as number;
        const si = score[p0 + i] as number;
        if (si === -Infinity) break;
        for (let j = 0; j < nCur; j++) {
          if (si <= (bestV[j] as number)) continue;
          const r = this.route(p0 + i, s * K + j, cap);
          if (!(r <= cap)) continue;
          const v = si - Math.abs(r - gc) * invBeta;
          if (v > (bestV[j] as number)) {
            bestV[j] = v;
            bestArg[j] = i;
          }
        }
      }
      let any = false;
      for (let j = 0; j < nCur; j++) {
        const cj = s * K + j;
        const d = cD[cj] as number;
        const arg = bestArg[j] as number;
        score[cj] = (bestV[j] as number) - d * d * inv2s2;
        back[cj] = arg;
        if (arg >= 0) any = true;
      }
      if (!any) {
        // Chain break: restart from the emissions alone.
        for (let j = 0; j < nCur; j++) {
          const d = cD[s * K + j] as number;
          score[s * K + j] = -d * d * inv2s2;
        }
      }
    }
    // 3. Backtrack, chain by chain from the end.
    const pick = this.pick;
    for (let s = steps - 1; s >= 0;) {
      let j = 0;
      for (let k = 1; k < (cN[s] as number); k++)
        if ((score[s * K + k] as number) > (score[s * K + j] as number)) j = k;
      for (; s >= 0; s--) {
        pick[s] = j;
        const b = back[s * K + j] as number;
        if (b < 0) {
          s--;
          break;
        }
        j = b;
      }
    }
    // 4. Route edges in order, with the offset range each one is covered over.
    if (this.chosen.length < steps) this.chosen = new Int32Array(steps * 2);
    const g = this.g;
    let n = 0;
    let last = -1;
    const push = (e: number) => {
      if (e === last) return;
      if (n === this.path.length) this.path = growI32(this.path, n * 2);
      this.path[n++] = e;
      last = e;
    };
    // Band class of the step into s: 0 running, 1 walking, -1 neither (or not banded: 0). Speed
    // is the displacement over at least HMM_SPEED_WINDOW_S back from s (or the step itself if
    // longer), so GPS jitter while standing does not read as moving; a step that is itself
    // faster than MAX_RUN_SPEED is cycling.
    const stepClass = (s: number): number => {
      if (!banded) return 0;
      const ts = this.sT[s] as number;
      let j = this.sIdx[s - 1] as number;
      const from = Math.min(this.sT[s - 1] as number, ts - HMM_SPEED_WINDOW_S * 1000);
      while (j > 0 && ((piece[j - 1] as TrackPoint).t as number) >= from) j--;
      // Mean of up to HMM_SPEED_SMOOTH fixes at each end of the window (not overlapping).
      // Near the trace start the window cannot reach back far enough: extend it forward.
      let i1 = this.sIdx[s] as number;
      const t0j = (piece[j] as TrackPoint).t as number;
      while (
        i1 + 1 < piece.length &&
        ((piece[i1] as TrackPoint).t as number) - t0j < HMM_SPEED_WINDOW_S * 1000
      )
        i1++;
      const k = Math.max(1, Math.min(HMM_SPEED_SMOOTH, (i1 - j + 1) >> 1));
      let la0 = 0;
      let lo0 = 0;
      let t0 = 0;
      let la1 = 0;
      let lo1 = 0;
      let t1 = 0;
      for (let q = 0; q < k; q++) {
        const a = piece[j + q] as TrackPoint;
        const b = piece[i1 - q] as TrackPoint;
        la0 += a.lat;
        lo0 += a.lon;
        t0 += a.t as number;
        la1 += b.lat;
        lo1 += b.lon;
        t1 += b.t as number;
      }
      const dt = (t1 - t0) / k / 1000;
      const dtOwn = (ts - (this.sT[s - 1] as number)) / 1000;
      if (!(dt > 0) || !(dtOwn > 0)) return -1;
      // The step itself at cycling speed counts nothing, even if the window averages it down.
      const own =
        haversine(
          this.sLat[s - 1] as number,
          this.sLon[s - 1] as number,
          this.sLat[s] as number,
          this.sLon[s] as number,
        ) / dtOwn;
      if (own > MAX_RUN_SPEED) return -1;
      const b = speedBand(haversine(la0 / k, lo0 / k, la1 / k, lo1 / k) / dt);
      return b === BAND_RUN ? 0 : b === BAND_WALK ? 1 : -1;
    };
    for (let s = 0; s < steps; s++) {
      const cj = s * K + (pick[s] as number);
      const e = this.cE[cj] as number;
      const oj = this.cOff[cj] as number;
      this.chosen[s] = e;
      if (s > 0 && (back[cj] as number) >= 0) {
        const cls = stepClass(s);
        const ci = (s - 1) * K + (pick[s - 1] as number);
        const ei = this.cE[ci] as number;
        const oi = this.cOff[ci] as number;
        const gc = haversine(
          this.sLat[s - 1] as number,
          this.sLon[s - 1] as number,
          this.sLat[s] as number,
          this.sLon[s] as number,
        );
        this.route(ci, cj, 2 * gc + 50);
        const a = this.viaA;
        const b = this.viaB;
        if (a < 0) this.cover(e, oi, oj, cls);
        else {
          this.cover(ei, oi, (g.u[ei] as number) === a ? 0 : (g.len[ei] as number), cls);
          if (a !== b) {
            // Edges a → b from a's tree, collected backwards from b.
            const slot = this.treeSlot[a] as number;
            let m = 0;
            for (let x = b; x !== a;) {
              const pe = this.treePred(slot, x);
              if (m === this.revBuf.length) this.revBuf = growI32(this.revBuf, m * 2);
              this.revBuf[m++] = pe;
              x = otherEnd(g, pe, x);
            }
            while (m > 0) {
              const pe = this.revBuf[--m] as number;
              this.cover(pe, 0, g.len[pe] as number, cls);
              push(pe);
            }
          }
          this.cover(e, (g.u[e] as number) === b ? 0 : (g.len[e] as number), oj, cls);
        }
      } else {
        if (s > 0) push(-1);
        // Chain start: the class of the step out of it, if the chain goes on.
        const next = s + 1 < steps && (back[(s + 1) * K + (pick[s + 1] as number)] as number) >= 0;
        this.cover(e, oj, oj, !banded ? 0 : next ? stepClass(s + 1) : -1);
      }
      push(e);
    }
    // 5. Drop edges the route only touches: a junction grazed (in and out through one node) or
    // the tip of a chain's end edge. Chains stay connected (a graze leaves through its entry).
    // A chain that stays on one edge keeps it (a short piece: as much evidence as v1 has).
    let w = 0;
    const stamp = this.pieceStamp;
    const E = g.edgeCount;
    if (this.pathW.length < this.path.length) this.pathW = new Float32Array(this.path.length);
    for (let cs = 0; cs < n;) {
      let ce = cs;
      while (ce < n && (this.path[ce] as number) >= 0) ce++;
      const single = ce - cs === 1;
      const w0 = w;
      for (let r = cs; r < ce; r++) {
        const e = this.path[r] as number;
        const need = Math.min(HMM_COVER_SHARE * (g.len[e] as number), HMM_COVER_M);
        let wt = 0;
        for (let c = 1; c >= 0; c--) {
          const k = c * E + e;
          if (this.covStamp[k] !== stamp) continue;
          if (single || (this.covHi[k] as number) - (this.covLo[k] as number) >= need)
            wt = c === 0 ? 1 : walkWeight;
        }
        if (wt <= 0) continue;
        if (w > w0 && this.path[w - 1] === e) continue;
        this.path[w] = e;
        this.pathW[w++] = wt;
      }
      if (w > w0 && ce < n) {
        this.path[w] = -1;
        this.pathW[w++] = 0;
      }
      cs = ce + 1;
    }
    if (w > 0 && (this.path[w - 1] as number) < 0) w--;
    return w;
  }

  /** Extends edge e's covered offset range in band class `cls` by [a, b]; cls < 0: no-op. */
  private cover(e: number, a: number, b: number, cls: number): void {
    if (cls < 0) return;
    const k = cls * this.g.edgeCount + e;
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    if (this.covStamp[k] !== this.pieceStamp) {
      this.covStamp[k] = this.pieceStamp;
      this.covLo[k] = lo;
      this.covHi[k] = hi;
      return;
    }
    if (lo < (this.covLo[k] as number)) this.covLo[k] = lo;
    if (hi > (this.covHi[k] as number)) this.covHi[k] = hi;
  }

  private growSteps(steps: number): void {
    const K = HMM_MAX_CANDIDATES;
    this.cE = growI32(this.cE, steps * K);
    this.cD = growF64(this.cD, steps * K);
    this.cOff = growF64(this.cOff, steps * K);
    this.score = growF64(this.score, steps * K);
    this.back = growI32(this.back, steps * K);
    this.cN = growI32(this.cN, steps);
    this.pick = growI32(this.pick, steps);
    this.sLat = growF64(this.sLat, steps);
    this.sLon = growF64(this.sLon, steps);
    this.sT = growF64(this.sT, steps);
    this.sIdx = growI32(this.sIdx, steps);
  }

  /**
   * Shortest route (metres) between candidates `ci` and `cj` (flat indices), exact up to `cap`;
   * anything longer may come back as Infinity. Sets `viaA/viaB` to the nodes it passes.
   */
  private route(ci: number, cj: number, cap: number): number {
    const g = this.g;
    const ei = this.cE[ci] as number;
    const ej = this.cE[cj] as number;
    const oi = this.cOff[ci] as number;
    const oj = this.cOff[cj] as number;
    const ui = g.u[ei] as number;
    const vi = g.v[ei] as number;
    const uj = g.u[ej] as number;
    const vj = g.v[ej] as number;
    const xu = oi;
    const xv = (g.len[ei] as number) - oi;
    const yu = oj;
    const yv = (g.len[ej] as number) - oj;
    let best = ei === ej ? Math.abs(oi - oj) : Infinity;
    let A = -1;
    let B = -1;
    // Through a shared end node.
    if (ui === uj && xu + yu < best) {
      best = xu + yu;
      A = B = ui;
    }
    if (ui === vj && xu + yv < best) {
      best = xu + yv;
      A = B = ui;
    }
    if (vi === uj && xv + yu < best) {
      best = xv + yu;
      A = B = vi;
    }
    if (vi === vj && xv + yv < best) {
      best = xv + yv;
      A = B = vi;
    }
    // Through distinct end nodes, only when a straight-line bound leaves room for a shorter one.
    for (let k = 0; k < 4; k++) {
      const a = k < 2 ? ui : vi;
      const b = k % 2 === 0 ? uj : vj;
      if (a === b) continue;
      const x = k < 2 ? xu : xv;
      const y = k % 2 === 0 ? yu : yv;
      if (x + y >= best || x + y > cap) continue;
      const dx = ((g.lon[a] as number) - (g.lon[b] as number)) * this.idx.proj.mPerDegLon;
      const dy = ((g.lat[a] as number) - (g.lat[b] as number)) * this.idx.proj.mPerDegLat;
      // Edge lengths are haversine sums, so ≥ the straight line (0.99: projection error).
      const lb = x + y + 0.99 * Math.sqrt(dx * dx + dy * dy);
      if (lb >= best || lb > cap) continue;
      const d = this.treeDist(a, b, cap - x - y);
      if (x + d + y < best) {
        best = x + d + y;
        A = a;
        B = b;
      }
    }
    this.viaA = A;
    this.viaB = B;
    return best;
  }

  /** Distance a → b if ≤ `need` (exact), else possibly Infinity. Trees are cached per piece. */
  private treeDist(a: number, b: number, need: number): number {
    let slot = this.treeStamp[a] === this.pieceStamp ? (this.treeSlot[a] as number) : -1;
    if (slot < 0 || (this.tRadius[slot] as number) < need) {
      slot = this.buildTree(a, need + 20);
      // A rebuilt tree has a new pool range: drop a stale loaded copy.
      if (this.lkSlot[0] === slot) this.lkSlot[0] = -1;
      if (this.lkSlot[1] === slot) this.lkSlot[1] = -1;
    }
    let w = this.lkSlot[0] === slot ? 0 : this.lkSlot[1] === slot ? 1 : -1;
    if (w < 0) {
      w = this.lkNext;
      this.lkNext ^= 1;
      const id = (this.lkId[w] as number) + 1;
      this.lkId[w] = id;
      const st = this.lkStamp;
      const ix = this.lkIdx;
      const base = w * this.g.nodeCount;
      const s = this.tStart[slot] as number;
      const end = s + (this.tLen[slot] as number);
      for (let k = s; k < end; k++) {
        const n = this.poolNode[k] as number;
        st[base + n] = id;
        ix[base + n] = k;
      }
      this.lkSlot[w] = slot;
    }
    const at = w * this.g.nodeCount + b;
    return this.lkStamp[at] === this.lkId[w]
      ? (this.poolDist[this.lkIdx[at] as number] as number)
      : Infinity;
  }

  /** Pred edge of node x in tree `slot` (x must be in it). */
  private treePred(slot: number, x: number): number {
    const s = this.tStart[slot] as number;
    const end = s + (this.tLen[slot] as number);
    for (let k = s; k < end; k++) if (this.poolNode[k] === x) return this.poolPred[k] as number;
    throw new Error("node not in tree");
  }

  /** Dijkstra from node `a` up to `radius` m, settled nodes appended to the pool. */
  private buildTree(a: number, radius: number): number {
    const g = this.g;
    const { dist, visit, predE, heap } = this;
    const stamp = ++this.visitStamp;
    let slot = this.treeStamp[a] === this.pieceStamp ? (this.treeSlot[a] as number) : -1;
    if (slot < 0) {
      slot = this.slots++;
      if (slot === this.tStart.length) {
        this.tStart = growI32(this.tStart, slot * 2);
        this.tLen = growI32(this.tLen, slot * 2);
        this.tRadius = growF64(this.tRadius, slot * 2);
      }
      this.treeStamp[a] = this.pieceStamp;
      this.treeSlot[a] = slot;
    }
    const start = this.poolLen;
    heap.clear();
    dist[a] = 0;
    visit[a] = stamp;
    predE[a] = -1;
    heap.push(0, a);
    // visit[n] = stamp: reached; -stamp: settled.
    while (heap.size > 0) {
      const n = heap.pop();
      const dn = heap.lastKey;
      if (visit[n] === -stamp || dn > (dist[n] as number)) continue;
      if (dn > radius) break;
      visit[n] = -stamp;
      if (this.poolLen === this.poolNode.length) {
        const m = this.poolLen * 2;
        this.poolNode = growI32(this.poolNode, m);
        this.poolDist = growF64(this.poolDist, m);
        this.poolPred = growI32(this.poolPred, m);
      }
      this.poolNode[this.poolLen] = n;
      this.poolDist[this.poolLen] = dn;
      this.poolPred[this.poolLen] = predE[n] as number;
      this.poolLen++;
      for (let k = g.adjStart[n] as number; k < (g.adjStart[n + 1] as number); k++) {
        const e = g.adjEdge[k] as number;
        const o = otherEnd(g, e, n);
        if (visit[o] === -stamp) continue;
        const nd = dn + (g.len[e] as number);
        if (visit[o] !== stamp || nd < (dist[o] as number)) {
          visit[o] = stamp;
          dist[o] = nd;
          predE[o] = e;
          heap.push(nd, o);
        }
      }
    }
    this.tStart[slot] = start;
    this.tLen[slot] = this.poolLen - start;
    this.tRadius[slot] = radius;
    return slot;
  }
}

/**
 * Map-matching + aggregation. Each running piece adds 1 to every edge it touches (once), each
 * walking piece WALK_WEIGHT; untimed points add 0.3 per point, normalised by edge length (per 10 m)
 * to undo sampling density. Returns raw weighted counts per edge.
 *
 * With `hmm`, timed pieces (running and walking) use the HMM matcher (v2), so the edges on the
 * route between matched points count too. Without it, and always for untimed points, v1: the
 * nearest edge within 25 m (15 m untimed) whose direction is within ±45° of the heading.
 */
export function matchCounts(
  g: Graph,
  idx: EdgeIndex,
  timed: Segment[],
  untimed: TrackPoint[],
  walk: Segment[] = [],
  walkWeight = WALK_WEIGHT,
  hmm: HmmMatcher | null = null,
): Float32Array {
  const counts = new Float32Array(g.edgeCount);
  // Edge e was last hit by piece number stamp[e] - 1 (typed array instead of a Set per piece).
  const stamp = new Int32Array(g.edgeCount);
  let n = 0;
  const addPiece = (piece: Segment, w: number) => {
    n++;
    if (hmm) {
      const len = hmm.matchPiece(piece);
      const path = hmm.path;
      for (let i = 0; i < len; i++) {
        const e = path[i] as number;
        if (e >= 0 && stamp[e] !== n) {
          stamp[e] = n;
          counts[e] = (counts[e] as number) + w;
        }
      }
      return;
    }
    for (let i = 0; i < piece.length; i++) {
      const p = piece[i] as TrackPoint;
      const a = piece[Math.max(0, i - 1)] as TrackPoint;
      const b = piece[Math.min(piece.length - 1, i + 1)] as TrackPoint;
      const heading = haversine(a.lat, a.lon, b.lat, b.lon) > 3 ? bearing(a, b) : null;
      const e = idx.match(p.lat, p.lon, MATCH_TIMED_M, heading);
      if (e >= 0 && stamp[e] !== n) {
        stamp[e] = n;
        counts[e] = (counts[e] as number) + w;
      }
    }
  };
  for (const piece of timed) addPiece(piece, 1);
  if (walkWeight > 0) for (const piece of walk) addPiece(piece, walkWeight);
  addUntimed(g, idx, untimed, counts);
  return counts;
}

/**
 * Untimed points (v1, ≤ 15 m, no heading): 0.3 per point, per 10 m of edge.
 * @deprecated Diagnostics only: production passes no untimed points (ADR 0005 run-only).
 */
function addUntimed(g: Graph, idx: EdgeIndex, untimed: TrackPoint[], counts: Float32Array): void {
  const untimedHits = new Float32Array(g.edgeCount);
  for (const p of untimed) {
    const e = idx.match(p.lat, p.lon, MATCH_UNTIMED_M, null);
    if (e >= 0) untimedHits[e] = (untimedHits[e] as number) + 1;
  }
  for (let e = 0; e < g.edgeCount; e++) {
    const u = untimedHits[e] as number;
    if (u > 0)
      counts[e] =
        (counts[e] as number) + (UNTIMED_WEIGHT * u) / Math.max(1, (g.len[e] as number) / 10);
  }
}

/**
 * Whole timed traces for the HMM (ADR 0005): the runs of `runTraces` (deduped, stitched, run
 * classifier), split only at gaps over 2 min (or time going backwards) and at untimed points.
 * Pieces need ≥ 2 points. Fully untimed segments go to `untimed`; `rejected` counts non-runs.
 */
export function timedTraces(segs: Segment[]): {
  traces: Segment[];
  untimed: TrackPoint[];
  rejected: number;
} {
  const traces: Segment[] = [];
  const { runs, untimed, rejected } = runTraces(segs);
  for (const seg of runs) {
    let cur: Segment = [];
    for (const p of seg) {
      const prev = cur[cur.length - 1];
      const dt = prev && p.t !== null ? (p.t - (prev.t as number)) / 1000 : 0;
      if (p.t === null || dt < 0 || dt > MAX_GAP_S) {
        if (cur.length >= 2) traces.push(cur);
        cur = [];
        if (p.t === null) continue;
      }
      cur.push(p);
    }
    if (cur.length >= 2) traces.push(cur);
  }
  return { traces, untimed, rejected };
}

/**
 * HMM over whole traces (`timedTraces`), each matched step weighted by its own speed band
 * (running 1, walking `walkWeight`, standing and cycling 0), once per trace per edge; untimed
 * points as v1. Returns raw weighted counts per edge.
 */
export function matchCountsTraces(
  g: Graph,
  idx: EdgeIndex,
  hmm: HmmMatcher,
  traces: Segment[],
  untimed: TrackPoint[],
  walkWeight = WALK_WEIGHT,
): Float32Array {
  const counts = new Float32Array(g.edgeCount);
  const stamp = new Int32Array(g.edgeCount);
  let n = 0;
  for (const trace of traces) {
    n++;
    const len = hmm.matchPiece(trace, true, walkWeight);
    for (let i = 0; i < len; i++) {
      const e = hmm.path[i] as number;
      if (e >= 0 && stamp[e] !== n) {
        stamp[e] = n;
        counts[e] = (counts[e] as number) + (hmm.pathW[i] as number);
      }
    }
  }
  addUntimed(g, idx, untimed, counts);
  return counts;
}

/** pop = min(1, log1p(count) / p95(log1p(count > 0))). */
export function normalisePopularity(counts: Float32Array): Float32Array {
  const logs: number[] = [];
  for (const c of counts) if (c > 0) logs.push(Math.log1p(c));
  const out = new Float32Array(counts.length);
  if (logs.length === 0) return out;
  logs.sort((a, b) => a - b);
  const p95 = logs[Math.min(logs.length - 1, Math.floor(0.95 * (logs.length - 1)))] as number;
  for (let i = 0; i < counts.length; i++) {
    const c = counts[i] as number;
    out[i] = c > 0 ? Math.min(1, Math.log1p(c) / Math.max(p95, 1e-6)) : 0;
  }
  return out;
}

/**
 * Decodes a pre-baked cell file (ADR 0003): `{"segments": [{"timed": bool, "p": [...]}]}` with
 * delta-encoded 1e-5° integers and, for timed segments, epoch seconds. Malformed segments are
 * skipped.
 */
export function decodePrebakedCell(json: unknown): Segment[] {
  const raw = (json as { segments?: unknown } | null)?.segments;
  if (!Array.isArray(raw)) return [];
  const out: Segment[] = [];
  for (const s of raw as unknown[]) {
    const obj = s as { timed?: unknown; p?: unknown } | null;
    if (!obj || !Array.isArray(obj.p)) continue;
    const p = obj.p as unknown[];
    const timed = obj.timed === true;
    const stride = timed ? 3 : 2;
    if (p.length === 0 || p.length % stride !== 0 || !p.every((v) => Number.isInteger(v))) continue;
    const seg: Segment = [];
    let lat = 0;
    let lon = 0;
    let t = 0;
    for (let i = 0; i < p.length; i += stride) {
      lat += p[i] as number;
      lon += p[i + 1] as number;
      if (timed) t += p[i + 2] as number;
      // Divide (not multiply by 1e-5) so 5932620 decodes to exactly 59.3262.
      seg.push({ lat: lat / 1e5, lon: lon / 1e5, t: timed ? t * 1000 : null });
    }
    out.push(seg);
  }
  return out;
}

/** Full pipeline: GPX pages → g.pop. Returns the number of matched edges. */
export function applyPopularity(g: Graph, gpxPages: string[]): number {
  return applyPopularitySegments(g, gpxPages.flatMap(parseTrackpointsGpx));
}

/**
 * Parsed segments (live GPX or pre-baked) → map matching → normalisation → g.pop. Returns the
 * number of matched edges. Only runs count (`runTraces`), and only their running-speed steps.
 * Matchers (ADR 0005): "hmm" (default) runs the HMM on whole run traces, weighting each step by
 * its speed band; "hmm-pieces" runs it on each running piece; "v1" is nearest-edge matching.
 */
export function applyPopularitySegments(
  g: Graph,
  segs: Segment[],
  matcher: "hmm" | "hmm-pieces" | "v1" = "hmm",
): number {
  const idx = new EdgeIndex(g);
  let counts: Float32Array;
  // Run-only (ADR 0005, 2026-11-04): untimed points and non-run traces are dropped, and only
  // running-speed steps count (WALK_WEIGHT = 0).
  if (matcher === "hmm") {
    const { traces } = timedTraces(segs);
    counts = matchCountsTraces(g, idx, new HmmMatcher(g, idx), traces, [], WALK_WEIGHT);
  } else {
    const { timed, walk } = runningPieces(runTraces(segs).runs);
    const hmm = matcher === "hmm-pieces" ? new HmmMatcher(g, idx) : null;
    counts = matchCounts(g, idx, timed, [], walk, WALK_WEIGHT, hmm);
  }
  const pop = normalisePopularity(counts);
  g.pop.set(pop);
  let n = 0;
  for (const p of pop) if (p > 0) n++;
  return n;
}
